import { useEffect, useMemo } from "react";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import { useSelector } from "react-redux";
import { dispatch } from "../../../state/store";
import { setIsFeaturesActive, setManageType, setSelectedApp } from "../../../state/ducks";

import { t } from "../../../services";
import { appManageTypes, routePaths, strCode } from "../../../consts";
import { useTableData } from "../../../hooks";

import {
  FlexContainers,
  Paragraph2,
  Table,
  TablePagination,
  WrapperContainer
} from "../../../components";
import { AppsTableColumns } from "./AppsTableColumns";
import { AppsTableWrapper } from "./styled";

const AppsTable = ({ appsData, searchValue }) => {
  const loading = useSelector((store) => store.apps?.loading);
  const error = useSelector((store) => store.apps?.error);
  const appsMarkets = useSelector((store) => store.apps?.appsMarkets);

  const history = useHistory();

  const data = useTableData({ loading, data: appsData });

  const columns = useMemo(() => AppsTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: "name",
            asc: true
          }
        ]
      }
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    rows,
    setFilter,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    setPageSize,
    state: { pageIndex, pageSize }
  } = tableInstance;

  const onRowClick = async (app) => {
    const appFromData = appsData.find((item) => item.name === app.name);
    const appName = Object.keys(appsMarkets).find((item) => item === app.appType);

    dispatch(setIsFeaturesActive(appFromData.defaultSettings.length > 0));
    dispatch(setManageType(appManageTypes.EDIT));
    dispatch(
      setSelectedApp({
        ...appFromData,
        marketConfigs: appsMarkets[appName],
        groupConfigs: [],
        emailMessage: appFromData.emailMessage || ""
      })
    );
    history.push(routePaths.CREATE_APP);
  };

  useEffect(() => {
    setFilter("name", searchValue);
  }, [searchValue]);

  // loading with Object.keys(appsMarkets).length > 0
  return (
    <WrapperContainer width="100%">
      {error ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.USERS_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        appsData && (
          <>
            <AppsTableWrapper>
              <Table
                tableInstance={tableInstance}
                searchedText={searchValue}
                searchInfo={`${t(strCode.NO_APP_NAME_FOUND)} '${searchValue}'.`}
                searchCheckInfo={t(strCode.CHECK_APP_NAME_MESSAGE)}
                onRowClick={onRowClick}
                tooltipLabel={t(strCode.SETTINGS_NAV_BTN)}
                isLoading={loading || !Object.keys(appsMarkets).length}
                tableKey="apps-table"
                hasPagination
              />
            </AppsTableWrapper>

            <TablePagination
              tableKey="apps-table"
              tableSize={rows?.length}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              onPrevPage={previousPage}
              onNextPage={nextPage}
              onFirstPage={() => gotoPage(0)}
              onLastPage={() => gotoPage(pageCount - 1)}
            />
          </>
        )
      )}
    </WrapperContainer>
  );
};

AppsTable.propTypes = {
  appsData: PropTypes.arrayOf(
    PropTypes.shape({
      appType: PropTypes.string.isRequired,
      availableDashboards: PropTypes.arrayOf(PropTypes.string).isRequired,
      link: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  searchValue: PropTypes.string.isRequired
};

export default AppsTable;
